import React, { useState } from "react";
import GenericButton from "@command-center-app/shared/ui/GenericButton";
import { colorPalette } from "@command-center-app/utils/colorPalette";
import { Add, ChevronLeft, ChevronRight } from "@mui/icons-material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Box, Divider, IconButton, TextField, Typography } from "@mui/material";
import CustomModal from "../../shared/ui/GenericModal";
import { handleNumericKeyDown } from "@command-center-app/utils/common.util";
interface GenericDecorationProps {
  surchargeItem?: any;
  surchargeIndex?: number;
  setFieldValue?: any;
  style?: any;
}

const GenericDecoration: React.FC<GenericDecorationProps> = ({
  surchargeItem,
  surchargeIndex,
  setFieldValue,
  style,
}) => {
  const [openDeleteModel, setOpenDeleteModel] = useState<boolean>(false);
  const [quantityBreakIndexToDelete, setQuantityBreakIndexToDelete] = useState<
    number | null
  >(null);
  if (!surchargeItem?.calculationMethod) return null;

  const handleCloseModal = () => {
    setOpenDeleteModel(false);
    setQuantityBreakIndexToDelete(null);
  };

  const handleDeleteQuantityBreak = () => {
    if (quantityBreakIndexToDelete === null) return;

    const newMatrix = surchargeItem.pricingMatrix.filter(
      (_: any, i: number) => i !== quantityBreakIndexToDelete
    );

    setFieldValue(`surcharge[${surchargeIndex}].pricingMatrix`, newMatrix);

    handleCloseModal();
  };

  return (
    <Box sx={{ border: style.grayBorder, borderRadius: 2 }}>
      <Box sx={{ overflowX: "auto", whiteSpace: "nowrap" }}>
        <Box sx={{ minWidth: "fit-content" }}>
          {/* Header */}
          <Box
            sx={{ display: "flex", gap: 2, alignItems: "center", padding: 2 }}
          >
            <Box
              sx={{
                ...style.contentCenter,
                width: "142px",
                height: "48px",
                flexShrink: 0,
              }}
            >
              <Typography variant="subtitle1">Quantity break</Typography>
            </Box>
            <Box
              sx={{
                ...style.contentCenter,
                gap: 1,
                border: style.grayBorder,
                width: "142px",
                height: "48px",
                borderRadius: "5px",
                flexShrink: 0,
              }}
            >
              <Typography variant="body2" color="text.disabled">
                Charge
              </Typography>
            </Box>
          </Box>

          <Divider />

          {/* Pricing matrix rows */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              padding: 2,
            }}
          >
            {surchargeItem.pricingMatrix?.map((item: any, idx: number) => {
              const prevMin =
                idx === 0
                  ? 1
                  : surchargeItem.pricingMatrix[idx - 1].parameters[0].minimum;
              const nextMin =
                idx + 1 < surchargeItem.pricingMatrix.length
                  ? surchargeItem.pricingMatrix[idx + 1].parameters[0].minimum
                  : 10000;
              const allowedMin = idx === 0 ? 1 : prevMin + 1;
              const allowedMax = nextMin - 1;
              const currentMin = item.parameters[0]?.minimum ?? allowedMin;
              const disableDecrement = currentMin <= allowedMin;
              const disableIncrement = currentMin >= allowedMax;

              const decrementMin = () => {
                if (!disableDecrement) {
                  setFieldValue(
                    `surcharge[${surchargeIndex}].pricingMatrix[${idx}].parameters[0].minimum`,
                    currentMin - 1
                  );
                }
              };

              const incrementMin = () => {
                if (!disableIncrement) {
                  setFieldValue(
                    `surcharge[${surchargeIndex}].pricingMatrix[${idx}].parameters[0].minimum`,
                    currentMin + 1
                  );
                }
              };

              const onInputChange = (
                e: React.ChangeEvent<HTMLInputElement>
              ) => {
                const raw = e.target.value;
                if (!/^\d*$/.test(raw)) return;
                setFieldValue(
                  `surcharge[${surchargeIndex}].pricingMatrix[${idx}].parameters[0].minimum`,
                  raw === "" ? "" : parseInt(raw, 10)
                );
              };

              const onInputBlur = (
                e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                let val = parseInt(e.target.value, 10);
                if (isNaN(val) || val < 1) val = 1;
                setFieldValue(
                  `surcharge[${surchargeIndex}].pricingMatrix[${idx}].parameters[0].minimum`,
                  val
                );
              };

              return (
                <Box
                  key={idx}
                  sx={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      alignItems: "center",
                      minWidth: "fit-content",
                    }}
                  >
                    {/* Quantity input */}
                    <Box
                      sx={{
                        border: style.grayBorder,
                        color: colorPalette.colorGrayishBlue,
                        borderRadius: "5px",
                        width: "142px",
                        height: "48px",
                        display: "flex",
                        justifyContent: "space-around",
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <IconButton
                        size="small"
                        disabled={disableDecrement}
                        onClick={decrementMin}
                      >
                        <ChevronLeft fontSize="small" />
                      </IconButton>

                      <TextField
                        type="text"
                        value={currentMin}
                        onChange={onInputChange}
                        onBlur={onInputBlur}
                        inputProps={{
                          min: allowedMin,
                          max: allowedMax,
                          style: { textAlign: "center" },
                        }}
                        sx={{
                          width: "60px",
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": { border: "none" },
                            "&:hover fieldset": { border: "none" },
                            "&.Mui-focused fieldset": { border: "none" },
                          },
                        }}
                      />

                      <IconButton
                        size="small"
                        disabled={disableIncrement}
                        onClick={incrementMin}
                      >
                        <ChevronRight fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* Price input */}
                    <Box
                      sx={{
                        ...style.contentCenter,
                        width: "142px",
                        height: "48px",
                        padding: "0px",
                        flexShrink: 0,
                      }}
                    >
                      <TextField
                        type="text"
                        inputMode="decimal"
                        fullWidth
                        value={item.price?.[0]?.value ?? ""}
                        onChange={(e: any) => {
                          let val = e.target.value;
                          const parts = val.split(".");
                          if (parts.length > 2) {
                            val = parts[0] + "." + parts.slice(1).join("");
                          }
                          val = val.replace(/[^\d.]/g, "");
                          if (/^0\d/.test(val)) {
                            val = val.replace(/^0+/, "");
                          }

                          setFieldValue(
                            `surcharge[${surchargeIndex}].pricingMatrix[${idx}].price[0].value`,
                            val
                          );
                        }}
                        onBlur={(e: any) =>
                          setFieldValue(
                            `surcharge[${surchargeIndex}].pricingMatrix[${idx}].price[0].value`,
                            parseFloat(e.target.value) || 0
                          )
                        }
                        sx={{
                          "& .MuiOutlinedInput-input": { padding: "12px" },
                          height: "48px",
                        }}
                        inputProps={{ min: 0 }}
                        InputProps={{
                          startAdornment: (
                            <Typography
                              sx={{
                                mr: 1,
                                color: colorPalette.colorSoftLightGray,
                              }}
                            >
                              $
                            </Typography>
                          ),
                        }}
                        onKeyDown={handleNumericKeyDown(true)}
                        onPaste={(e) => {
                          const pasted = e.clipboardData.getData("text");
                          if (!/^\d*\.?\d*$/.test(pasted)) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Delete row */}
                  <IconButton
                    size="small"
                    onClick={() => {
                      setQuantityBreakIndexToDelete(idx);
                      setOpenDeleteModel(true);
                    }}
                    disabled={surchargeItem.pricingMatrix.length === 1}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}

            {/* Add break button */}
            <GenericButton
              text="Add break"
              variant="outlined"
              type="button"
              style={{
                height: "48px",
                width: "142px",
                borderColor: colorPalette.colorAccent,
              }}
              hasIcon
              startIcon
              backgroundColor="none"
              textColor={colorPalette.colorAccent}
              icon={<Add />}
              onClick={() => {
                const currentMatrix = surchargeItem.pricingMatrix || [];
                const lastItem =
                  currentMatrix[currentMatrix.length - 1] || null;
                const lastMin = lastItem?.parameters[0]?.minimum ?? 0;
                const newMin = lastMin + 1;

                const newItem = {
                  parameters: [{ minimum: newMin, maximum: 9999 }],
                  price: [{ value: 0 }],
                };

                if (lastItem) {
                  setFieldValue(
                    `surcharge[${surchargeIndex}].pricingMatrix[${
                      currentMatrix.length - 1
                    }].parameters[0].maximum`,
                    newMin - 1
                  );
                }

                setFieldValue(`surcharge[${surchargeIndex}].pricingMatrix`, [
                  ...currentMatrix,
                  newItem,
                ]);
              }}
            />
          </Box>
        </Box>
      </Box>
      <CustomModal
        width={600}
        handleClose={handleCloseModal}
        open={openDeleteModel}
        modalTitle="Delete quantity break"
        footerButtons={
          <GenericButton
            text="Delete"
            variant="contained"
            onClick={handleDeleteQuantityBreak}
            backgroundColor={colorPalette.error}
          />
        }
      >
        <Box>
          <Typography sx={{ p: 2 }}>
            Are you sure you want to delete this quantity break?
          </Typography>
        </Box>
      </CustomModal>
    </Box>
  );
};

export default GenericDecoration;
